import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../../../../store/index';
import { useTranslation } from 'react-i18next';
import { fetchPut } from '../../../../../helpers/fetching';
import { setSnackbar } from '../../../../../store';
import Projection from './Projection';
import ProjectionIcons from '../ProjectionIcons/ProjectionIcons';
import { useAppliedEvaluation } from '../../../../../helpers/hooks/useEvaluation';
import { breastVolumeInfo } from './common/ProjectionCommon';
import { qualityClassColor } from '../../../../../helpers/manageColors';
import ProjectionFabs from './common/ProjectionFabs';
import { Typography, Tooltip, IconButton } from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

const useStyles = makeStyles((theme) => ({
  projection_card_container: {
    display: 'flex',
    width: '100%',
    justifyContent: 'inherit',
    borderRadius: '5px',
    minHeight: '10vh',
    overflow: 'hidden',
    height: '100%',
    alignItems: 'center',
  },
  projection_evaluations_upper: {
    position: 'absolute',
    top: '6px',
    color: '#d3d3d3',
    cursor: 'pointer',
    zIndex: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    borderRadius: '5px',
    padding: '1px 6px 0 6px',
  },

  navigation_arrows_container: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 10,
  },

  navigation_arrow: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: 'gray',
    pointerEvents: 'auto',
    '&:hover': {
      color: 'white',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
    '&:active': {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  },

  arrow_up: {
    top: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
  },

  arrow_down: {
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
  },

  arrow_left: {
    top: '50%',
    transform: 'translateY(-50%)',
  },

  arrow_right: {
    top: '50%',
    transform: 'translateY(-50%)',
  },
}));

const ProjectionsCard = ({ left, proj_name, onLoad, uid, src, invertedColor, setInvertedColor }) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Store state
  const results = useSelector((state) => state.results);

  const {
    projections,
    shown_quality,
    applied_quality,
    shown_density,
    applied_density,
    shown_opacities,
    applied_opacities,
    shown_microcalc,
    applied_microcalc,
  } = results;
  const { tabsIndex } = useSelector((state) => state.gui);
  const { qualityInDrawingMode, diagnosticsInRevisionMode, qualityInRevisionMode, densityInRevisionMode } =
    useSelector((state) => state.labeling);

  // Local state
  const [fetchingEval, setFetchingEval] = useState(false);
  const [cursor, setCursor] = useState('default');
  const [moveImageFunction, setMoveImageFunction] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(1);
  const density_module = useSelector((state) => state.global_settings.premium_features.density_module);
  const quality_module = useSelector((state) => state.global_settings.premium_features.quality_module);
  const densityShown = shown_density || applied_density;
  const qualityShown = shown_quality || applied_quality;
  const opacitiesShown = shown_opacities || applied_opacities;
  const microcalcShown = shown_microcalc || applied_microcalc;
  const resultQualityEvaluation = useAppliedEvaluation('quality', qualityShown, qualityInRevisionMode);
  const resultDensityEvaluation = useAppliedEvaluation('density', densityShown, densityInRevisionMode);
  const resultOpacitiesEvaluation = useAppliedEvaluation(
    'opacities',
    opacitiesShown,
    diagnosticsInRevisionMode
  );
  const resultMicrocalcEvaluation = useAppliedEvaluation(
    'microcalc',
    microcalcShown,
    diagnosticsInRevisionMode
  );

  // Current Breast Volume
  const pastBreastVolume = projections?.[proj_name]?.pastBreastVolumes;
  const mloBreastArea =
    proj_name === 'rcc'
      ? resultQualityEvaluation?.rmlo?.BreastVolume
      : resultQualityEvaluation?.lmlo?.BreastVolume;
  const ccBreastArea =
    proj_name === 'rmlo'
      ? resultQualityEvaluation?.rcc?.BreastVolume
      : resultQualityEvaluation?.lcc?.BreastVolume;

  const breastAreaDifference = (
    resultQualityEvaluation?.[proj_name]?.BreastVolume -
    (proj_name === 'rcc' || proj_name === 'lcc' ? mloBreastArea : ccBreastArea)
  ).toFixed(0);

  useEffect(() => {
    if (qualityInDrawingMode) setCursor('crosshair');
    else if (diagnosticsInRevisionMode) setCursor('crosshair');
    else setCursor('default');
  }, [tabsIndex, qualityInDrawingMode, diagnosticsInRevisionMode]);

  // Handlers
  const setEvaluated = async (state) => {
    setFetchingEval(true);
    const response = await fetchPut(
      'evaluate',
      {
        projection_id: projections?.[proj_name]?.uid,
        state,
      },
      'bbox'
    );

    if (response.success) {
      dispatch(setSnackbar({ msg: 'ResultView.evaluation_success', severity: 'success' }));
    } else {
      dispatch(setSnackbar({ msg: 'ResultView.evaluation_error', severity: 'error' }));
    }
    setFetchingEval(false);
  };

  const alignProjections = (proj_name) => {
    if (proj_name === 'rcc')
      return { justify: 'flex-end', align: 'flex-end', marginRight: 'min(0.3vh, 0.3vw)', marginLeft: '0' };
    if (proj_name === 'lcc')
      return { justify: 'flex-start', align: 'flex-end', marginRight: '0', marginLeft: 'min(0.3vh, 0.3vw)' };
    if (proj_name === 'rmlo')
      return { justify: 'flex-end', align: 'flex-start', marginRight: 'min(0.3vh, 0.3vw)', marginLeft: '0' };
    if (proj_name === 'lmlo')
      return {
        justify: 'flex-start',
        align: 'flex-start',
        marginRight: '0',
        marginLeft: 'min(0.3vh, 0.3vw)',
      };
    return { justify: 'center', align: 'center', marginRight: '0', marginLeft: '0' };
  };

  // Handle moveImage function from Projection component
  const handleMoveImage = (moveImageFn, zoom) => {
    setMoveImageFunction(() => moveImageFn);
    setCurrentZoom(zoom);
  };

  return (
    <div
      style={{
        background: invertedColor?.[proj_name] ? 'white' : 'black',
        height: '100%',
        width: '90%',
        borderRadius: '5px',
        display: 'flex',
        justifyContent: alignProjections(proj_name).justify,
        alignItems: alignProjections(proj_name).align,
        marginRight: alignProjections(proj_name).marginRight,
        marginLeft: alignProjections(proj_name).marginLeft,
      }}>
      <div className={classes.projection_card_container} style={{ cursor: cursor }}>
        {/* Breast Volume */}
        {quality_module && (tabsIndex === 1 || (tabsIndex === 2 && qualityInRevisionMode)) &&
          !!resultQualityEvaluation?.[proj_name]?.BreastVolume && (
            <div
              className={classes.projection_evaluations_upper}
              style={{
                left: left ? undefined : '45px',
                right: left ? '45px' : undefined,
                flexDirection: left ? 'row-reverse' : 'row',
                backgroundColor: `${qualityClassColor(
                  resultQualityEvaluation?.[proj_name]?.VolumeProjectionDiff,
                  theme
                )}30`,
              }}>
              {breastVolumeInfo(
                resultQualityEvaluation,
                breastAreaDifference,
                pastBreastVolume,
                proj_name,
                left,
                theme,
                classes,
                t,
                dispatch,
                actions
              )}
            </div>
          )}

        <Projection
          proj_name={proj_name}
          onLoad={onLoad}
          uid={uid}
          qualityEvaluation={quality_module ? resultQualityEvaluation?.[proj_name] : null}
          densityEvaluation={density_module ? resultDensityEvaluation?.[proj_name] : null}
          diagnosticsEvaluation={
            resultOpacitiesEvaluation?.[proj_name] || resultMicrocalcEvaluation?.[proj_name]
          }
          setEvaluated={setEvaluated}
          left={left}
          src={src}
          fetchingEval={fetchingEval}
          projection={projections?.[proj_name]}
          invertedColor={invertedColor}
          setInvertedColor={setInvertedColor}
          onMoveImage={handleMoveImage}
        />

        {/* Navigation Arrows - Only show when zoomed */}
        {currentZoom > 1 && moveImageFunction && (
          <div className={classes.navigation_arrows_container}>
            <IconButton
              className={`${classes.navigation_arrow} ${classes.arrow_up}`}
              onClick={() => moveImageFunction('up')}
              size='small'>
              <ArrowUpwardIcon fontSize='small' />
            </IconButton>

            <IconButton
              className={`${classes.navigation_arrow} ${classes.arrow_down}`}
              onClick={() => moveImageFunction('down')}
              size='small'>
              <ArrowDownwardIcon fontSize='small' />
            </IconButton>

            <IconButton
              className={`${classes.navigation_arrow} ${classes.arrow_left}`}
              style={{ left: proj_name === 'lcc' || proj_name === 'lmlo' ? '10px' : '14%' }}
              onClick={() => moveImageFunction('left')}
              size='small'>
              <ArrowBackIcon fontSize='small' />
            </IconButton>

            <IconButton
              className={`${classes.navigation_arrow} ${classes.arrow_right}`}
              style={{ right: proj_name === 'lcc' || proj_name === 'lmlo' ? '14%' : '10px' }}
              onClick={() => moveImageFunction('right')}
              size='small'>
              <ArrowForwardIcon fontSize='small' />
            </IconButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectionsCard;
